/**
 * Day-parting API client (feature #74) — dashboard side.
 *
 * Mirrors the HTTP contract defined in
 * `apps/api/src/routes/campaign-dayparting.ts`:
 *
 *   GET    /api/v1/campaign-dayparting/:campaignId         list rules
 *   POST   /api/v1/campaign-dayparting                     create 1 rule
 *   PUT    /api/v1/campaign-dayparting/:campaignId         atomic replace
 *   DELETE /api/v1/campaign-dayparting/:campaignId         drop all rules
 *
 * Rule semantics (canonical, mirrored here so the grid can reason locally):
 *   dayOfWeek = 0-6 (Sunday=0) or 'all'
 *   startHour / endHour ∈ [0, 23]
 *     start < end   → same-day window [start, end)
 *     start = end   → 24h full day
 *     start > end   → wraps past midnight
 *   tz = IANA zone string
 *
 * Only this file (plus the route + grid) may talk to the network for this
 * feature. Keep it thin and typed.
 */

import { apiClient } from "../../api-client";

// ---------------------------------------------------------------------------
// Domain types
// ---------------------------------------------------------------------------

export type DayOfWeek = 0 | 1 | 2 | 3 | 4 | 5 | 6;
export const ALL_DAYS = "all" as const;
export type DayOfWeekInput = DayOfWeek | typeof ALL_DAYS;

export interface DaypartingRule {
	id: string;
	campaign_id: string;
	day_of_week: DayOfWeekInput;
	start_hour: number;
	end_hour: number;
	tz: string;
	created_at: number;
	updated_at: number;
}

export interface DaypartingRuleInput {
	dayOfWeek: DayOfWeekInput;
	startHour: number;
	endHour: number;
	tz: string;
}

// ---------------------------------------------------------------------------
// Validation — a tiny Zod-shaped helper so we don't need to pull in the
// full zod package just for 4 fields. Returns `{ok,value}` or `{ok,issues}`
// so callers can render inline error messages.
// ---------------------------------------------------------------------------

export type ValidationIssue = { path: string; message: string };
export type ValidationResult<T> =
	| { ok: true; value: T }
	| { ok: false; issues: ValidationIssue[] };

export function isValidHour(n: unknown): n is number {
	return (
		typeof n === "number" &&
		Number.isInteger(n) &&
		n >= 0 &&
		n <= 23
	);
}

export function isValidDayOfWeek(v: unknown): v is DayOfWeekInput {
	if (v === ALL_DAYS) return true;
	return (
		typeof v === "number" &&
		Number.isInteger(v) &&
		v >= 0 &&
		v <= 6
	);
}

export function isValidTimezone(v: unknown): v is string {
	if (typeof v !== "string" || v.trim().length === 0) return false;
	try {
		new Intl.DateTimeFormat("en-US", { timeZone: v });
		return true;
	} catch {
		return false;
	}
}

export function validateRuleInput(
	raw: Partial<DaypartingRuleInput>,
): ValidationResult<DaypartingRuleInput> {
	const issues: ValidationIssue[] = [];
	if (!isValidDayOfWeek(raw.dayOfWeek)) {
		issues.push({
			path: "dayOfWeek",
			message: "Day of week must be 0-6 or 'all'.",
		});
	}
	if (!isValidHour(raw.startHour)) {
		issues.push({
			path: "startHour",
			message: "Start hour must be an integer 0-23.",
		});
	}
	if (!isValidHour(raw.endHour)) {
		issues.push({
			path: "endHour",
			message: "End hour must be an integer 0-23.",
		});
	}
	if (!isValidTimezone(raw.tz)) {
		issues.push({
			path: "tz",
			message: "Timezone must be a valid IANA zone.",
		});
	}
	if (issues.length > 0) return { ok: false, issues };
	return {
		ok: true,
		value: raw as DaypartingRuleInput,
	};
}

// ---------------------------------------------------------------------------
// Timezone catalogue — pulled from the browser if available, else a safe
// fallback list. We filter to canonical IANA zones and sort alphabetically.
// ---------------------------------------------------------------------------

const FALLBACK_TIMEZONES: readonly string[] = [
	"UTC",
	"Africa/Cairo",
	"Africa/Johannesburg",
	"Africa/Lagos",
	"America/Anchorage",
	"America/Argentina/Buenos_Aires",
	"America/Bogota",
	"America/Chicago",
	"America/Denver",
	"America/Halifax",
	"America/Havana",
	"America/Lima",
	"America/Los_Angeles",
	"America/Mexico_City",
	"America/New_York",
	"America/Phoenix",
	"America/Santiago",
	"America/Sao_Paulo",
	"America/Toronto",
	"America/Vancouver",
	"Asia/Bangkok",
	"Asia/Dubai",
	"Asia/Hong_Kong",
	"Asia/Jakarta",
	"Asia/Jerusalem",
	"Asia/Karachi",
	"Asia/Kolkata",
	"Asia/Kuala_Lumpur",
	"Asia/Manila",
	"Asia/Riyadh",
	"Asia/Seoul",
	"Asia/Shanghai",
	"Asia/Singapore",
	"Asia/Taipei",
	"Asia/Tel_Aviv",
	"Asia/Tokyo",
	"Atlantic/Reykjavik",
	"Australia/Adelaide",
	"Australia/Brisbane",
	"Australia/Melbourne",
	"Australia/Perth",
	"Australia/Sydney",
	"Europe/Amsterdam",
	"Europe/Athens",
	"Europe/Berlin",
	"Europe/Brussels",
	"Europe/Bucharest",
	"Europe/Budapest",
	"Europe/Copenhagen",
	"Europe/Dublin",
	"Europe/Helsinki",
	"Europe/Istanbul",
	"Europe/Lisbon",
	"Europe/London",
	"Europe/Madrid",
	"Europe/Moscow",
	"Europe/Oslo",
	"Europe/Paris",
	"Europe/Prague",
	"Europe/Rome",
	"Europe/Stockholm",
	"Europe/Vienna",
	"Europe/Warsaw",
	"Europe/Zurich",
	"Pacific/Auckland",
	"Pacific/Fiji",
	"Pacific/Honolulu",
];

export function listSupportedTimezones(): string[] {
	// `Intl.supportedValuesOf` is the right answer when available (ES2022+).
	const sv = (
		Intl as unknown as {
			supportedValuesOf?: (k: string) => string[];
		}
	).supportedValuesOf;
	if (typeof sv === "function") {
		try {
			const list = sv("timeZone");
			if (Array.isArray(list) && list.length > 0) return list;
		} catch {
			/* fall through */
		}
	}
	return [...FALLBACK_TIMEZONES];
}

export function getBrowserTimezone(): string {
	try {
		const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
		if (tz && isValidTimezone(tz)) return tz;
	} catch {
		/* ignored */
	}
	return "UTC";
}

// ---------------------------------------------------------------------------
// HTTP wrappers
// ---------------------------------------------------------------------------

const BASE = "/campaign-dayparting";

export interface ListRulesResponse {
	rules: DaypartingRule[];
}

export async function listDaypartingRules(
	campaignId: string,
): Promise<DaypartingRule[]> {
	const res = await apiClient.get<ListRulesResponse>(
		`${BASE}/${encodeURIComponent(campaignId)}`,
	);
	return res?.rules ?? [];
}

export async function createDaypartingRule(
	campaignId: string,
	input: DaypartingRuleInput,
): Promise<DaypartingRule> {
	const res = await apiClient.post<{ rule: DaypartingRule }>(BASE, {
		campaignId,
		...input,
	});
	return res.rule;
}

export async function replaceDaypartingRules(
	campaignId: string,
	rules: DaypartingRuleInput[],
): Promise<DaypartingRule[]> {
	const res = await apiClient.put<ListRulesResponse>(
		`${BASE}/${encodeURIComponent(campaignId)}`,
		{ rules },
	);
	return res?.rules ?? [];
}

export async function deleteDaypartingRules(
	campaignId: string,
): Promise<number> {
	const res = await apiClient.delete<{ deleted: number }>(
		`${BASE}/${encodeURIComponent(campaignId)}`,
	);
	return res?.deleted ?? 0;
}

// ---------------------------------------------------------------------------
// Grid projection helpers
// ---------------------------------------------------------------------------

/**
 * 7 rows × 24 cols matrix of booleans. Rows are ordered Mon..Sun so it
 * reads like a calendar week; internally the API uses Sunday=0. Use the
 * helpers below to bridge the two conventions.
 */
export type GridMatrix = boolean[][]; // [rowIndex 0..6][hour 0..23]

export const GRID_ROWS = 7;
export const GRID_COLS = 24;

/** Mon, Tue, ..., Sun → 1, 2, ..., 0 (API convention). */
export const ROW_TO_DOW: readonly DayOfWeek[] = [1, 2, 3, 4, 5, 6, 0];
export const ROW_LABELS: readonly string[] = [
	"Mon",
	"Tue",
	"Wed",
	"Thu",
	"Fri",
	"Sat",
	"Sun",
];

export function emptyGrid(): GridMatrix {
	return Array.from({ length: GRID_ROWS }, () =>
		Array.from({ length: GRID_COLS }, () => false),
	);
}

export function cloneGrid(grid: GridMatrix): GridMatrix {
	return grid.map((row) => [...row]);
}

function setHoursForDay(
	grid: GridMatrix,
	rowIndex: number,
	startHour: number,
	endHour: number,
) {
	if (rowIndex < 0 || rowIndex >= GRID_ROWS) return;
	// start === end → full 24h in canonical API semantics.
	if (startHour === endHour) {
		for (let h = 0; h < GRID_COLS; h++) grid[rowIndex][h] = true;
		return;
	}
	if (startHour < endHour) {
		for (let h = startHour; h < endHour; h++) grid[rowIndex][h] = true;
		return;
	}
	// Wrap-past-midnight: [startHour,24) for today's row, [0,endHour) for
	// the NEXT row (because the window continues into tomorrow).
	for (let h = startHour; h < GRID_COLS; h++) grid[rowIndex][h] = true;
	const nextRow = (rowIndex + 1) % GRID_ROWS;
	for (let h = 0; h < endHour; h++) grid[nextRow][h] = true;
}

/** Project a list of rules onto the 7×24 grid for UI consumption. */
export function rulesToGrid(rules: readonly DaypartingRule[]): GridMatrix {
	const grid = emptyGrid();
	for (const r of rules) {
		const dow = r.day_of_week;
		if (dow === ALL_DAYS) {
			for (let row = 0; row < GRID_ROWS; row++) {
				setHoursForDay(grid, row, r.start_hour, r.end_hour);
			}
			continue;
		}
		const rowIndex = ROW_TO_DOW.indexOf(dow as DayOfWeek);
		if (rowIndex >= 0) {
			setHoursForDay(grid, rowIndex, r.start_hour, r.end_hour);
		}
	}
	return grid;
}

/**
 * Compress the 7×24 grid back into a minimal rule set. For each row we walk
 * the 24 hours and emit one rule per contiguous run of true cells. This
 * keeps the payload small (max 7 × ~24 = 168 rules, usually far fewer) and
 * round-trips cleanly through the API's same-day window semantics.
 */
export function gridToRules(
	grid: GridMatrix,
	tz: string,
): DaypartingRuleInput[] {
	const out: DaypartingRuleInput[] = [];
	for (let row = 0; row < GRID_ROWS; row++) {
		const hours = grid[row];
		let run: number | null = null;
		for (let h = 0; h < GRID_COLS; h++) {
			if (hours[h] && run === null) run = h;
			if (!hours[h] && run !== null) {
				out.push({
					dayOfWeek: ROW_TO_DOW[row],
					startHour: run,
					endHour: h,
					tz,
				});
				run = null;
			}
		}
		if (run !== null) {
			// Trailing run ending at 24: represent as [run, 0) via wrap?
			// Simpler: emit [run, 23] as start<end window by clamping to 0
			// (start === end) only when run === 0 (full day). Otherwise use
			// start < end with end = 24 — but API caps end at 23, so encode
			// hour 23 as end=0 wrap... simplest: represent full 24h when
			// run === 0, else emit [run, 0) wrap.
			if (run === 0) {
				// Full 24 hours → canonical full-day rule.
				out.push({
					dayOfWeek: ROW_TO_DOW[row],
					startHour: 0,
					endHour: 0,
					tz,
				});
			} else {
				// [run, 24) → wrap form [run, 0). endHour = 0 means "until
				// start of next day" which the API treats as wrap and the
				// next row would own [0, X) — but since we emit per-row
				// runs, wrap past midnight is rare. Store as start<end with
				// end=23 capped is lossy; instead use wrap form [run, 0).
				out.push({
					dayOfWeek: ROW_TO_DOW[row],
					startHour: run,
					endHour: 0,
					tz,
				});
			}
		}
	}
	return out;
}
